/**
 * Library tab — paginated browse of research_sources with filters,
 * top-tag column, and detail modal (full 7-dim quality + 15-dim contexts +
 * citation history).
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Eye, Filter, Search, X } from 'lucide-react';
import { adminFetch } from '../../../utils/adminApi';
import type { PaginatedResponse, ResearchSourceListItem } from './types';

const PAGE_SIZE = 50;

function badgeColor(score: number | null): string {
  if (score == null) return 'bg-gray-100 text-gray-600';
  if (score >= 80) return 'bg-emerald-100 text-emerald-800';
  if (score >= 60) return 'bg-blue-100 text-blue-800';
  return 'bg-amber-100 text-amber-800';
}

interface DetailResponse extends ResearchSourceListItem {
  abstract?: string;
  url?: string;
  quality_breakdown?: Record<string, number> | null;
  // NK-CAT (practitioner) fields — populated when knowledge_type IS practitioner_*
  key_findings?: string[] | null;
  source_urls?: string[] | null;
  source_tier?: number | null;
  ckg_category?: string | null;
  ckg_subcategory?: string | null;
  ckg_topic_id?: string | null;
  freshness_date?: string | null;
  next_refresh_at?: string | null;
  refresh_cadence_days?: number | null;
  consensus_level?: string | null;
  sex_hormone_contexts: string[];
  sport_contexts: string[];
  intervention_contexts: string[];
  lens_contexts: string[];
  phase_contexts: string[];
  mechanism_contexts: string[];
  nutrition_contexts: string[];
  sleep_contexts: string[];
  recovery_contexts: string[];
  psychological_contexts: string[];
  environmental_contexts: string[];
  hydration_contexts: string[];
  not_applicable_to_profiles: string[];
  recent_citations: Array<{
    id: string;
    event_type: string;
    event_title?: string;
    intelligence_engine: string | null;
    intelligence_subcategory?: string | null;
    event_date: string;
  }>;
  curation_history: Array<{
    id: string;
    action: string;
    admin_username: string | null;
    notes: string | null;
    created_at: string;
  }>;
  usage_summary: {
    total_uses?: number;
    uses_30d?: number;
    distinct_users?: number;
    distinct_event_types?: number;
  };
}

function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ['rk', 'source-detail', id],
    queryFn: () => adminFetch<DetailResponse>(`/api/admin/researcher-knowledge/sources/${id}`),
  });

  const allContexts: Array<[string, string[]]> = useMemo(() => {
    if (!detail.data) return [];
    return [
      ['Conditions', detail.data.condition_contexts || []],
      ['Demographics', detail.data.demographic_contexts || []],
      ['Sex / Hormone', detail.data.sex_hormone_contexts || []],
      ['Goals', detail.data.goal_contexts || []],
      ['Sports', detail.data.sport_contexts || []],
      ['Interventions', detail.data.intervention_contexts || []],
      ['Lenses', detail.data.lens_contexts || []],
      ['Phases', detail.data.phase_contexts || []],
      ['Mechanisms', detail.data.mechanism_contexts || []],
      ['Nutrition', detail.data.nutrition_contexts || []],
      ['Sleep', detail.data.sleep_contexts || []],
      ['Recovery', detail.data.recovery_contexts || []],
      ['Psychological', detail.data.psychological_contexts || []],
      ['Environmental', detail.data.environmental_contexts || []],
      ['Hydration', detail.data.hydration_contexts || []],
      ['Applies to Profiles', detail.data.applies_to_profiles || []],
      ['Excluded Profiles', detail.data.not_applicable_to_profiles || []],
    ];
  }, [detail.data]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 flex items-start justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900 pr-8">
            {detail.data?.title || 'Loading…'}
          </h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="space-y-6 p-6">
          {detail.isLoading && <div className="text-center text-gray-500">Loading…</div>}
          {detail.data && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeColor(detail.data.quality_score)}`}>
                  Quality {detail.data.quality_score ?? '—'}
                </span>
                <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                  {detail.data.knowledge_type}
                </span>
                {detail.data.evidence_strength && (
                  <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    {detail.data.evidence_strength}
                  </span>
                )}
                {detail.data.methodology_type && (
                  <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-800">
                    {detail.data.methodology_type}
                  </span>
                )}
                {detail.data.review_status && detail.data.review_status !== 'approved' && (
                  <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                    {detail.data.review_status}
                  </span>
                )}
                {detail.data.created_by_agent && (
                  <span className="rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                    via {detail.data.created_by_agent}
                  </span>
                )}
              </div>

              {/* Academic-paper-shaped header (only for knowledge_type='research'
                  — NK-CAT practitioner rows don't populate these fields) */}
              {detail.data.knowledge_type === 'research' && (
                <div className="text-sm text-gray-700">
                  <div>{(detail.data.authors || []).join(', ')}</div>
                  <div className="text-gray-500">
                    {detail.data.journal} {detail.data.publication_year && `· ${detail.data.publication_year}`}
                  </div>
                  {detail.data.doi && (
                    <a
                      href={`https://doi.org/${detail.data.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      DOI: {detail.data.doi} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}

              {/* PK-CAT-shaped header (knowledge_type IN practitioner_*) */}
              {detail.data.knowledge_type !== 'research' && (
                <div className="space-y-2 rounded-md border border-purple-100 bg-purple-50/40 p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {detail.data.source_tier != null && (
                      <span className="rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-800">
                        Source tier {detail.data.source_tier}
                      </span>
                    )}
                    {detail.data.consensus_level && (
                      <span className="rounded-full bg-purple-50 px-2 py-0.5 text-purple-700">
                        Consensus: {detail.data.consensus_level}
                      </span>
                    )}
                    {detail.data.ckg_category && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">
                        {detail.data.ckg_category}
                        {detail.data.ckg_subcategory && ` › ${detail.data.ckg_subcategory}`}
                      </span>
                    )}
                  </div>
                  {(detail.data.freshness_date || detail.data.next_refresh_at || detail.data.refresh_cadence_days) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                      {detail.data.freshness_date && (
                        <span>
                          <span className="text-gray-500">Freshness:</span>{' '}
                          {new Date(detail.data.freshness_date).toLocaleDateString()}
                        </span>
                      )}
                      {detail.data.next_refresh_at && (
                        <span>
                          <span className="text-gray-500">Next refresh:</span>{' '}
                          {new Date(detail.data.next_refresh_at).toLocaleDateString()}
                        </span>
                      )}
                      {detail.data.refresh_cadence_days != null && (
                        <span>
                          <span className="text-gray-500">Cadence:</span>{' '}
                          every {detail.data.refresh_cadence_days}d
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Academic abstract */}
              {detail.data.knowledge_type === 'research' && detail.data.abstract && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Abstract</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.data.abstract}</p>
                </div>
              )}

              {/* PK-CAT body — key_findings (3-5 sentence takeaways) */}
              {detail.data.knowledge_type !== 'research' && detail.data.key_findings && detail.data.key_findings.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Key Findings</h4>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
                    {detail.data.key_findings.map((finding, i) => (
                      <li key={i} className="whitespace-pre-wrap">{finding}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* PK-CAT references (NK-CAT writes URLs to source_urls instead of DOI) */}
              {detail.data.knowledge_type !== 'research' && detail.data.source_urls && detail.data.source_urls.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                    References ({detail.data.source_urls.length})
                  </h4>
                  <ul className="space-y-1 text-sm">
                    {detail.data.source_urls.map((url, i) => (
                      <li key={i}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 break-all text-blue-600 hover:underline"
                        >
                          {url} <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {detail.data.quality_breakdown && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                    Quality Breakdown (7 dimensions)
                  </h4>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    {Object.entries(detail.data.quality_breakdown).map(([dim, score]) => (
                      <div key={dim} className="rounded border border-gray-100 bg-gray-50 px-3 py-2 text-xs">
                        <div className="font-medium text-gray-900">{dim}</div>
                        <div className="text-gray-600">{score}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                  Context Tags (15 dimensions + profiles)
                </h4>
                <div className="space-y-2">
                  {allContexts
                    .filter(([, tags]) => tags.length > 0)
                    .map(([label, tags]) => (
                      <div key={label} className="flex flex-wrap items-baseline gap-2">
                        <span className="min-w-[140px] text-xs font-medium text-gray-500">{label}</span>
                        <div className="flex flex-wrap gap-1">
                          {tags.map((tag) => (
                            <span key={tag} className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-800">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Usage Summary</h4>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className="rounded border border-gray-100 bg-gray-50 p-3 text-sm">
                    <div className="text-gray-500 text-xs">Total Uses</div>
                    <div className="font-semibold text-gray-900">{detail.data.usage_summary?.total_uses || 0}</div>
                  </div>
                  <div className="rounded border border-gray-100 bg-gray-50 p-3 text-sm">
                    <div className="text-gray-500 text-xs">Last 30 Days</div>
                    <div className="font-semibold text-gray-900">{detail.data.usage_summary?.uses_30d || 0}</div>
                  </div>
                  <div className="rounded border border-gray-100 bg-gray-50 p-3 text-sm">
                    <div className="text-gray-500 text-xs">Distinct Users</div>
                    <div className="font-semibold text-gray-900">{detail.data.usage_summary?.distinct_users || 0}</div>
                  </div>
                  <div className="rounded border border-gray-100 bg-gray-50 p-3 text-sm">
                    <div className="text-gray-500 text-xs">Event Types</div>
                    <div className="font-semibold text-gray-900">{detail.data.usage_summary?.distinct_event_types || 0}</div>
                  </div>
                </div>
              </div>

              {detail.data.recent_citations?.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                    Recent Citations (Last 50 Events)
                  </h4>
                  <div className="max-h-60 overflow-y-auto rounded border border-gray-100">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">When</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Event</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Engine</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {detail.data.recent_citations.map((c) => (
                          <tr key={c.id}>
                            <td className="px-3 py-2 text-gray-700">{new Date(c.event_date).toLocaleString()}</td>
                            <td className="px-3 py-2 text-gray-700">{c.event_type}</td>
                            <td className="px-3 py-2 text-gray-500">{c.intelligence_engine || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {detail.data.curation_history?.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Curation History</h4>
                  <ul className="space-y-1 text-xs">
                    {detail.data.curation_history.map((h) => (
                      <li key={h.id} className="text-gray-700">
                        <span className="font-medium">{h.action}</span> by{' '}
                        <span className="text-gray-500">{h.admin_username || 'system'}</span>
                        <span className="ml-2 text-gray-400">{new Date(h.created_at).toLocaleString()}</span>
                        {h.notes && <div className="text-gray-500 ml-4">{h.notes}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function LibraryTab() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [knowledgeType, setKnowledgeType] = useState<'all' | 'research' | 'practitioner'>('all');
  const [qualityMin, setQualityMin] = useState(0);
  const [methodologyType, setMethodologyType] = useState('');
  const [evidenceStrength, setEvidenceStrength] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [sortBy, setSortBy] = useState<'quality_desc' | 'recency' | 'usage_30d' | 'title'>('quality_desc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const queryParams = new URLSearchParams({
    knowledge_type: knowledgeType,
    quality_min: String(qualityMin),
    sort_by: sortBy,
    page: String(page),
    page_size: String(PAGE_SIZE),
  });
  if (q) queryParams.set('q', q);
  if (methodologyType) queryParams.set('methodology_type', methodologyType);
  if (evidenceStrength) queryParams.set('evidence_strength', evidenceStrength);
  if (reviewStatus) queryParams.set('review_status', reviewStatus);
  if (conditionFilter) queryParams.set('condition', conditionFilter);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['rk', 'sources', queryParams.toString()],
    queryFn: () =>
      adminFetch<PaginatedResponse<ResearchSourceListItem>>(
        `/api/admin/researcher-knowledge/sources?${queryParams}`
      ),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-4">
      {/* Search + filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search title or abstract…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        <select
          value={knowledgeType}
          onChange={(e) => { setKnowledgeType(e.target.value as typeof knowledgeType); setPage(1); }}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="all">All knowledge types</option>
          <option value="research">Academic research</option>
          <option value="practitioner">Practitioner knowledge</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="quality_desc">Quality (desc)</option>
          <option value="recency">Most recent publication</option>
          <option value="usage_30d">Used most (30d)</option>
          <option value="title">Title A→Z</option>
        </select>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
        >
          <Filter className="h-4 w-4" /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <label className="text-xs text-gray-500">Quality ≥</label>
              <input
                type="number"
                min={0}
                max={100}
                value={qualityMin}
                onChange={(e) => setQualityMin(Math.max(0, Math.min(100, +e.target.value || 0)))}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Methodology</label>
              <select value={methodologyType} onChange={(e) => setMethodologyType(e.target.value)}
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm">
                <option value="">Any</option>
                <option value="rct">RCT</option>
                <option value="meta_analysis">Meta-analysis</option>
                <option value="systematic_review">Systematic review</option>
                <option value="cohort">Cohort</option>
                <option value="case_control">Case-control</option>
                <option value="case_study">Case study</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Evidence strength</label>
              <select value={evidenceStrength} onChange={(e) => setEvidenceStrength(e.target.value)}
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm">
                <option value="">Any</option>
                <option value="strong">Strong</option>
                <option value="moderate">Moderate</option>
                <option value="emerging">Emerging</option>
                <option value="preliminary">Preliminary</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Review status</label>
              <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm">
                <option value="">Any</option>
                <option value="approved">Approved</option>
                <option value="flagged">Flagged</option>
                <option value="excluded">Excluded</option>
                <option value="pending_review">Pending review</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Condition tag (exact)</label>
              <input
                type="text"
                placeholder="e.g. lower_back_pain_chronic"
                value={conditionFilter}
                onChange={(e) => setConditionFilter(e.target.value)}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Quality</th>
              <th className="px-3 py-2 text-left">Evidence</th>
              <th className="px-3 py-2 text-left">Top Tags</th>
              <th className="px-3 py-2 text-right">Used 30d</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={7} className="p-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {isError && (
              <tr><td colSpan={7} className="p-8 text-center text-red-600">Failed to load.</td></tr>
            )}
            {data?.items.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900 line-clamp-2">{row.title}</div>
                  <div className="text-xs text-gray-500">
                    {row.journal} {row.publication_year && `· ${row.publication_year}`}
                    {row.pending_retag && (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">Pending retag</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                    {row.knowledge_type}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${badgeColor(row.quality_score)}`}>
                    {row.quality_score ?? '—'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {row.evidence_strength || '—'}
                  {row.methodology_type && <div className="text-gray-500">{row.methodology_type}</div>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {row.top_tags.map((tag) => (
                      <span key={tag} className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-800">
                        {tag}
                      </span>
                    ))}
                    {row.additional_tag_count > 0 && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                        +{row.additional_tag_count}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-gray-700">{row.usage_30d}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setSelectedId(row.id)}
                    className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs hover:bg-gray-100"
                  >
                    <Eye className="h-3 w-3" /> View
                  </button>
                </td>
              </tr>
            ))}
            {data?.items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-gray-500">No results.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            {(data.page - 1) * data.page_size + 1}–
            {Math.min(data.page * data.page_size, data.total)} of {data.total.toLocaleString()}
          </div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-2 py-1">
              Page {data.page} of {data.total_pages}
            </span>
            <button
              disabled={page >= data.total_pages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedId && <DetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
